import { Component, Input, OnInit } from '@angular/core';

import {
  EventQuestion,
  EventType,
  ScheduledEventsColumn
} from '../../../../../../../../models/event-type';
import { CustomCustomerField } from '../../../../../../../../models/organization-config';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { propExists, propExistsAndIsNonEmpty } from '../../../../../../../../utils';
import { getCustomerFieldLabel, getScheduledEventsColumnHeaderName } from '../../../../../../../../utils-models';
import { EventQuestionType, ScheduledEventsColumnType } from '../../../../../../../../models/event-type-enums';
import { ProfileField } from '../../../../../../../../models/organization-config-enums';


class AvailableColumnType {
  columnType: ScheduledEventsColumnType;
  name: string;
}


@Component({
  selector: 'app-add-scheduled-events-column-modal',
  templateUrl: './add-scheduled-events-column-modal.component.html',
  styleUrls: ['./add-scheduled-events-column-modal.component.css']
})
export class AddScheduledEventsColumnModalComponent implements OnInit {
  @Input() eventType: EventType;
  @Input() eventQuestions: EventQuestion[];
  @Input() customCustomerFields: CustomCustomerField[];
  @Input() savedCallback: (column: ScheduledEventsColumn) => undefined;

  public column: ScheduledEventsColumn = {
    column_type: null
  };

  public availableCapitanProfileFieldNames: ProfileField[] = [];
  public availableCustomCustomerFieldNames: string[] = [];
  public availableAttendeeQuestionNames: string[] = [];
  public availableColumnTypes: AvailableColumnType[] = [];

  public ScheduledEventsColumnType = ScheduledEventsColumnType;

  constructor(
    public activeModal: NgbActiveModal
  ) {}

  ngOnInit() {
    this.populateAvailableCapitanProfileFieldNames();
    this.populateAvailableCustomCustomerFieldNames();
    this.populateAvailableAttendeeQuestionNames();
    this.populateAvailableColumnTypes();
  }

  public isValid(): boolean {
    if (!propExists(this.column.column_type)) { return false; }

    if (this.column.column_type === ScheduledEventsColumnType.capitanProfileField) {
      if (!propExistsAndIsNonEmpty(this.column.profile_field_name)) {
        return false;
      }
    }
    if (this.column.column_type === ScheduledEventsColumnType.customCustomerField) {
      if (!propExistsAndIsNonEmpty(this.column.custom_field_name)) {
        return false;
      }
    }
    if (this.column.column_type === ScheduledEventsColumnType.attendeeQuestion) {
      if (!propExistsAndIsNonEmpty(this.column.question_name)) {
        return false;
      }
    }

    return true;
  }

  public save() {
    if (!this.isValid()) { return; }

    if (this.column.column_type !== ScheduledEventsColumnType.capitanProfileField) {
      this.column.profile_field_name = undefined;
    }
    if (this.column.column_type !== ScheduledEventsColumnType.customCustomerField) {
      this.column.custom_field_name = undefined;
    }
    if (this.column.column_type !== ScheduledEventsColumnType.attendeeQuestion) {
      this.column.question_name = undefined;
    }

    this.savedCallback(this.column);

    this.activeModal.dismiss();
  }

  public getThisCustomerFieldLabel(profileField: ProfileField) {
    return getCustomerFieldLabel(
      profileField,
      this.customCustomerFields
    );
  }

  private populateAvailableCapitanProfileFieldNames() {
    const existingFields = new Set<ProfileField>();

    for (const column of this.eventType.scheduled_events_columns) {
      if (column.column_type === ScheduledEventsColumnType.capitanProfileField) {
        existingFields.add(column.profile_field_name);
      }
    }

    for (const [, field] of Object.entries(ProfileField)) {
      if (field === ProfileField.name) { continue; }
      if (field === ProfileField.email) { continue; }
      if (field === ProfileField.customCustomerField) { continue; }
      if (!existingFields.has(field)) {
        this.availableCapitanProfileFieldNames.push(field);
      }
    }
  }

  private populateAvailableCustomCustomerFieldNames() {
    const existingFields = new Set<string>();

    for (const column of this.eventType.scheduled_events_columns) {
      if (column.column_type === ScheduledEventsColumnType.customCustomerField) {
        existingFields.add(column.custom_field_name);
      }
    }

    for (const field of this.customCustomerFields) {
      if (!existingFields.has(field.name)) {
        this.availableCustomCustomerFieldNames.push(field.name);
      }
    }
  }

  private populateAvailableAttendeeQuestionNames() {
    const existingQuestions = new Set<string>();

    for (const column of this.eventType.scheduled_events_columns) {
      if (column.column_type === ScheduledEventsColumnType.attendeeQuestion) {
        existingQuestions.add(column.question_name);
      }
    }

    for (const question of this.eventQuestions) {
      if (question.question_type === EventQuestionType.customCustomerField) { continue; }
      if (!existingQuestions.has(question.name)) {
        this.availableAttendeeQuestionNames.push(question.name);
      }
    }
  }

  private populateAvailableColumnTypes() {
    const reservationColumnTypes = [
      ScheduledEventsColumnType.reservationEmail,
      ScheduledEventsColumnType.payment,
      ScheduledEventsColumnType.feeType,
      ScheduledEventsColumnType.addOns,
      ScheduledEventsColumnType.bookedBy,
      ScheduledEventsColumnType.hasEmergencyContact,
      ScheduledEventsColumnType.medicalNotes
    ];
    for (const resColType of reservationColumnTypes) {
      if (!this.hasExistingColumnType(resColType)) {
        this.availableColumnTypes.push({
          columnType: resColType,
          name: getScheduledEventsColumnHeaderName({column_type: resColType})
        });
      }
    }
    if (this.availableCapitanProfileFieldNames.length > 0) {
      this.availableColumnTypes.push({
        columnType: ScheduledEventsColumnType.capitanProfileField,
        name: 'Capitan Profile Field'
      });
    }
    if (this.availableCustomCustomerFieldNames.length > 0) {
      this.availableColumnTypes.push({
        columnType: ScheduledEventsColumnType.customCustomerField,
        name: 'Custom Customer Field'
      });
    }
    if (this.availableAttendeeQuestionNames.length > 0) {
      this.availableColumnTypes.push({
        columnType: ScheduledEventsColumnType.attendeeQuestion,
        name: 'Attendee Question'
      });
    }
  }

  private hasExistingColumnType(columnType: ScheduledEventsColumnType): boolean {
    for (const column of this.eventType.scheduled_events_columns) {
      if (column.column_type === columnType) { return true; }
    }
    return false;
  }
}
